export interface Department {
  id: string;
  nameEn: string;
  nameFr: string;
  descEn: string;
  descFr: string;
  icon: string;
  color: string;
}

export interface Doctor {
  id: string;
  name: string;
  specialtyEn: string;
  specialtyFr: string;
  bioEn: string;
  bioFr: string;
  availability: string;
  experience: number;
  avatar: string;
  initials: string;
  color: string;
}

export interface NewsItem {
  id: string;
  titleEn: string;
  titleFr: string;
  excerptEn: string;
  excerptFr: string;
  date: string;
  category: string;
  image: string;
}

export interface Testimonial {
  name: string;
  textEn: string;
  textFr: string;
  rating: number;
  dept: string;
}

export const departments: Department[] = [
  {
    id: 'general',
    nameEn: 'General Medicine',
    nameFr: 'Médecine Générale',
    descEn: 'Comprehensive primary care and diagnosis for adults and elderly patients.',
    descFr: 'Soins primaires complets et diagnostic pour adultes et personnes âgées.',
    icon: '🏥',
    color: '#0B4F8A',
  },
  {
    id: 'surgery',
    nameEn: 'Surgery',
    nameFr: 'Chirurgie',
    descEn: 'General and specialized surgical procedures performed by expert surgeons.',
    descFr: 'Interventions chirurgicales générales et spécialisées réalisées par des chirurgiens experts.',
    icon: '⚕️',
    color: '#C62828',
  },
  {
    id: 'pediatrics',
    nameEn: 'Pediatrics',
    nameFr: 'Pédiatrie',
    descEn: 'Specialized healthcare for infants, children, and adolescents.',
    descFr: 'Soins de santé spécialisés pour nourrissons, enfants et adolescents.',
    icon: '👶',
    color: '#F57C00',
  },
  {
    id: 'gynecology',
    nameEn: 'Gynecology & Maternity',
    nameFr: 'Gynécologie & Maternité',
    descEn: "Women's health services including prenatal, delivery, and postnatal care.",
    descFr: 'Services de santé féminine incluant soins prénataux, accouchement et postnatal.',
    icon: '🤱',
    color: '#AD1457',
  },
  {
    id: 'emergency',
    nameEn: 'Emergency',
    nameFr: 'Urgences',
    descEn: '24/7 emergency medical services for all critical and urgent conditions.',
    descFr: 'Services médicaux d\'urgence 24h/24 pour toutes les situations critiques.',
    icon: '🚨',
    color: '#D32F2F',
  },
  {
    id: 'laboratory',
    nameEn: 'Laboratory',
    nameFr: 'Laboratoire',
    descEn: 'Full clinical laboratory services for accurate diagnosis and monitoring.',
    descFr: 'Services de laboratoire clinique complets pour un diagnostic et un suivi précis.',
    icon: '🧪',
    color: '#6A1B9A',
  },
  {
    id: 'pharmacy',
    nameEn: 'Pharmacy',
    nameFr: 'Pharmacie',
    descEn: 'Complete pharmacy services with a wide range of essential medications.',
    descFr: 'Services pharmaceutiques complets avec une large gamme de médicaments essentiels.',
    icon: '💊',
    color: '#00695C',
  },
  {
    id: 'radiology',
    nameEn: 'Radiology & Imaging',
    nameFr: 'Radiologie & Imagerie',
    descEn: 'X-ray, ultrasound, and advanced imaging for accurate diagnostic support.',
    descFr: 'Radiographie, échographie et imagerie avancée pour un soutien diagnostique précis.',
    icon: '🔬',
    color: '#1565C0',
  },
  {
    id: 'vaccination',
    nameEn: 'Vaccination & Prevention',
    nameFr: 'Vaccination & Prévention',
    descEn: 'Immunization programs and preventive care for all age groups.',
    descFr: 'Programmes de vaccination et soins préventifs pour tous les groupes d\'âge.',
    icon: '💉',
    color: '#2E7D32',
  },
  {
    id: 'internal',
    nameEn: 'Internal Medicine',
    nameFr: 'Médecine Interne',
    descEn: 'Diagnosis and treatment of complex adult diseases and chronic conditions.',
    descFr: 'Diagnostic et traitement des maladies adultes complexes et des affections chroniques.',
    icon: '❤️',
    color: '#BF360C',
  },
  {
    id: 'consultation',
    nameEn: 'Consultation Services',
    nameFr: 'Services de Consultation',
    descEn: 'Specialist consultations and second-opinion services across all disciplines.',
    descFr: 'Consultations spécialisées et avis dans toutes les disciplines.',
    icon: '📋',
    color: '#37474F',
  },
  {
    id: 'icu',
    nameEn: 'Intensive Care (ICU)',
    nameFr: 'Soins Intensifs (USI)',
    descEn: 'Critical care monitoring and treatment for severely ill patients.',
    descFr: 'Surveillance et traitement des soins intensifs pour les patients gravement malades.',
    icon: '🫀',
    color: '#880E4F',
  },
];

export const doctors: Doctor[] = [
  {
    id: '1',
    name: 'Dr. Emmanuel Nkouam',
    specialtyEn: 'General Surgery',
    specialtyFr: 'Chirurgie Générale',
    bioEn: 'Medical Director with 20+ years of surgical experience. Trained in France and Cameroon.',
    bioFr: 'Directeur médical avec 20+ ans d\'expérience chirurgicale. Formé en France et au Cameroun.',
    availability: 'Mon–Fri',
    experience: 20,
    avatar: '',
    initials: 'EN',
    color: '#0B4F8A',
  },
  {
    id: '2',
    name: 'Dr. Marie-Claire Fotso',
    specialtyEn: 'Gynecology & Obstetrics',
    specialtyFr: 'Gynécologie & Obstétrique',
    bioEn: 'Specialist in maternal health and high-risk pregnancies with 15 years of practice.',
    bioFr: 'Spécialiste en santé maternelle et grossesses à risque avec 15 ans de pratique.',
    availability: 'Mon–Sat',
    experience: 15,
    avatar: '',
    initials: 'MF',
    color: '#AD1457',
  },
  {
    id: '3',
    name: 'Dr. Jean-Paul Tchouanté',
    specialtyEn: 'Pediatrics',
    specialtyFr: 'Pédiatrie',
    bioEn: 'Pediatric specialist dedicated to child health. Champion of vaccination programs in the region.',
    bioFr: 'Spécialiste pédiatrique dédié à la santé de l\'enfant. Champion des programmes de vaccination.',
    availability: 'Mon–Fri',
    experience: 12,
    avatar: '',
    initials: 'JT',
    color: '#F57C00',
  },
  {
    id: '4',
    name: 'Dr. Aïcha Njoya',
    specialtyEn: 'Internal Medicine',
    specialtyFr: 'Médecine Interne',
    bioEn: 'Expert in chronic disease management, diabetes, and cardiovascular conditions.',
    bioFr: 'Experte en gestion des maladies chroniques, diabète et affections cardiovasculaires.',
    availability: 'Tue–Sat',
    experience: 10,
    avatar: '',
    initials: 'AN',
    color: '#BF360C',
  },
  {
    id: '5',
    name: 'Dr. Samuel Wouagap',
    specialtyEn: 'Radiology & Imaging',
    specialtyFr: 'Radiologie & Imagerie',
    bioEn: 'Radiologist specializing in ultrasound and diagnostic imaging with modern equipment.',
    bioFr: 'Radiologue spécialisé en échographie et imagerie diagnostique avec équipements modernes.',
    availability: 'Mon–Fri',
    experience: 8,
    avatar: '',
    initials: 'SW',
    color: '#1565C0',
  },
  {
    id: '6',
    name: 'Dr. Pauline Kamga',
    specialtyEn: 'General Medicine',
    specialtyFr: 'Médecine Générale',
    bioEn: 'General practitioner with expertise in tropical diseases and preventive medicine.',
    bioFr: 'Médecin généraliste avec expertise en maladies tropicales et médecine préventive.',
    availability: 'Mon–Sat',
    experience: 9,
    avatar: '',
    initials: 'PK',
    color: '#2E7D32',
  },
  {
    id: '7',
    name: 'Dr. Roger Tagne',
    specialtyEn: 'Emergency Medicine',
    specialtyFr: 'Médecine d\'Urgence',
    bioEn: 'Emergency medicine specialist ensuring rapid and effective care 24/7.',
    bioFr: 'Spécialiste en médecine d\'urgence assurant des soins rapides et efficaces 24h/24.',
    availability: 'Mon–Sun',
    experience: 11,
    avatar: '',
    initials: 'RT',
    color: '#D32F2F',
  },
  {
    id: '8',
    name: 'Dr. Cécile Nguimi',
    specialtyEn: 'Intensive Care',
    specialtyFr: 'Soins Intensifs',
    bioEn: 'ICU specialist with training in critical care and advanced life support.',
    bioFr: 'Spécialiste USI formée en soins critiques et support vital avancé.',
    availability: 'Mon–Fri',
    experience: 13,
    avatar: '',
    initials: 'CN',
    color: '#880E4F',
  },
];

export const newsItems: NewsItem[] = [
  {
    id: '1',
    titleEn: 'New Maternity Ward Opens — Enhanced Care for Mothers',
    titleFr: 'Nouvelle Maternité Inaugurée — Soins Améliorés pour les Mères',
    excerptEn: 'Our newly renovated maternity ward now features 40 additional beds, a modern delivery suite, and a neonatal care unit.',
    excerptFr: 'Notre maternité rénovée dispose désormais de 40 lits supplémentaires, d\'une salle d\'accouchement moderne et d\'une unité de soins néonatals.',
    date: '2024-03-15',
    category: 'Announcement',
    image: '#0B4F8A',
  },
  {
    id: '2',
    titleEn: 'Free Malaria Screening Campaign — March 2024',
    titleFr: 'Campagne de Dépistage Gratuit du Paludisme — Mars 2024',
    excerptEn: 'Join our free malaria screening campaign at the hospital and in partner health centers across Bafoussam.',
    excerptFr: 'Participez à notre campagne de dépistage gratuit du paludisme à l\'hôpital et dans les centres de santé partenaires de Bafoussam.',
    date: '2024-03-08',
    category: 'Campaign',
    image: '#2E7D32',
  },
  {
    id: '3',
    titleEn: 'Diabetes Prevention: Know Your Numbers',
    titleFr: 'Prévention du Diabète : Connaissez Vos Chiffres',
    excerptEn: 'With diabetes rising in Cameroon, our endocrinology team shares key tips for prevention and early detection.',
    excerptFr: 'Avec l\'augmentation du diabète au Cameroun, notre équipe d\'endocrinologie partage des conseils pour la prévention et la détection précoce.',
    date: '2024-02-20',
    category: 'Health Tip',
    image: '#F57C00',
  },
  {
    id: '4',
    titleEn: 'Childhood Vaccination Drive — Protect Your Child',
    titleFr: 'Campagne de Vaccination Infantile — Protégez Votre Enfant',
    excerptEn: 'Our pediatric team reminds parents of the vaccination schedule and announces extended vaccine clinic hours.',
    excerptFr: 'Notre équipe pédiatrique rappelle aux parents le calendrier vaccinal et annonce des horaires élargis de vaccination.',
    date: '2024-02-10',
    category: 'Vaccination',
    image: '#6A1B9A',
  },
  {
    id: '5',
    titleEn: 'World Cancer Day: Early Detection Saves Lives',
    titleFr: 'Journée Mondiale du Cancer : La Détection Précoce Sauve des Vies',
    excerptEn: 'On World Cancer Day, our oncology team offers free consultations and shares information about cancer prevention.',
    excerptFr: 'Pour la Journée Mondiale du Cancer, notre équipe d\'oncologie propose des consultations gratuites et partage des informations sur la prévention.',
    date: '2024-02-04',
    category: 'Awareness',
    image: '#C62828',
  },
  {
    id: '6',
    titleEn: 'Hand Hygiene: The First Line of Defense',
    titleFr: 'Hygiène des Mains : La Première Ligne de Défense',
    excerptEn: 'Proper hand hygiene prevents the spread of infections. Learn the WHO technique from our infection control team.',
    excerptFr: 'Une bonne hygiène des mains prévient la propagation des infections. Apprenez la technique OMS avec notre équipe.',
    date: '2024-01-28',
    category: 'Prevention',
    image: '#00695C',
  },
];

export const testimonials: Testimonial[] = [
  {
    name: 'Mama Adjoua K.',
    textEn: 'The maternity team was incredible. They took such good care of me and my baby. I am forever grateful to Dr. Fotso and all the nurses.',
    textFr: 'L\'équipe de maternité était incroyable. Ils ont pris grand soin de moi et de mon bébé. Je suis éternellement reconnaissante au Dr. Fotso et à toutes les infirmières.',
    rating: 5,
    dept: 'Gynecology',
  },
  {
    name: 'M. Pierre Nganou',
    textEn: 'I was treated for a serious condition and the doctors were very professional. The hospital is clean and the staff friendly. I highly recommend it.',
    textFr: 'J\'ai été traité pour une affection grave et les médecins étaient très professionnels. L\'hôpital est propre et le personnel accueillant. Je le recommande vivement.',
    rating: 5,
    dept: 'Internal Medicine',
  },
  {
    name: 'Mme. Christelle T.',
    textEn: 'My child was admitted to the pediatric ward and received excellent care. The pediatric team is wonderful with children.',
    textFr: 'Mon enfant a été admis en pédiatrie et a reçu d\'excellents soins. L\'équipe pédiatrique est merveilleuse avec les enfants.',
    rating: 4,
    dept: 'Pediatrics',
  },
];

export const emergencyNumbers = {
  main: '(+237) 233 44 10 00',
  ambulance: '(+237) 233 44 10 01',
  director: '(+237) 699 00 11 22',
};

export const hospitalInfo = {
  addressEn: 'Avenue de l\'Indépendance, Bafoussam, West Region, Cameroon',
  addressFr: 'Avenue de l\'Indépendance, Bafoussam, Région de l\'Ouest, Cameroun',
  email: 'info@hopital-bafoussam.cm',
  website: 'www.hopital-bafoussam.cm',
  founded: 1978,
};

export const stats = [
  { labelEn: 'Patients Served', labelFr: 'Patients Servis', value: '500K+' },
  { labelEn: 'Medical Specialists', labelFr: 'Spécialistes Médicaux', value: '80+' },
  { labelEn: 'Departments', labelFr: 'Départements', value: '12' },
  { labelEn: 'Years of Service', labelFr: 'Années de Service', value: '46' },
];
